import { useMemo } from 'react';

import { FilterOption } from '@/components/ui/custom/ComplexFilter/types';
import useBrains from '@/hooks/useBrains';
import useCollections from '@/hooks/useCollections';
import useDesks from '@/hooks/useDesks';
import useMembers from '@/hooks/useMembers';

import { useAllConnections } from './useAllConnections';
import { FILTER_KEY, FilterKey } from '../constants';

const toOptions = <T>(
  items: T[] | undefined,
  getValue: (item: T) => string,
  getLabel: (item: T) => string
): FilterOption[] =>
  (items ?? [])
    .map((item) => ({ value: getValue(item), label: getLabel(item) }))
    .sort((a, b) => a.label.localeCompare(b.label));

export interface ActivityLogFilterOptions {
  optionsByFilter: Record<FilterKey, FilterOption[]>;
  /** Whether each filter's source list has loaded — gates the "all selected → omit" rule. */
  loadedByFilter: Record<FilterKey, boolean>;
}

/** Loads the five filter sources and maps each to sorted `FilterOption[]`. */
export const useActivityLogFilterOptions = (): ActivityLogFilterOptions => {
  const { members } = useMembers();
  const { brains } = useBrains();
  const { collections } = useCollections();
  const { desks } = useDesks();
  const { connections, isLoading: connectionsLoading } = useAllConnections();

  return useMemo(() => {
    const optionsByFilter: Record<FilterKey, FilterOption[]> = {
      [FILTER_KEY.people]: toOptions(
        members,
        (m) => m.user_id,
        (m) => m.name
      ),
      [FILTER_KEY.brains]: toOptions(
        brains,
        (b) => b.brain_id,
        (b) => b.name
      ),
      [FILTER_KEY.collections]: toOptions(
        collections,
        (c) => c.collection_id,
        (c) => c.name
      ),
      [FILTER_KEY.desks]: toOptions(
        desks,
        (d) => d.desk_id,
        (d) => d.name
      ),
      [FILTER_KEY.connections]: toOptions(
        connections,
        (i) => i.integration_id,
        (i) => i.name
      ),
    };

    const loadedByFilter: Record<FilterKey, boolean> = {
      [FILTER_KEY.people]: members !== undefined,
      [FILTER_KEY.brains]: brains !== undefined,
      [FILTER_KEY.collections]: collections !== undefined,
      [FILTER_KEY.desks]: desks !== undefined,
      [FILTER_KEY.connections]: !connectionsLoading,
    };

    return { optionsByFilter, loadedByFilter };
  }, [members, brains, collections, desks, connections, connectionsLoading]);
};
